import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Icon from '../../components/AppIcon';
import { tiendaDb } from '../../lib/supabase';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const RED = '#F87171';
const ORANGE = '#FB923C';
const FONDO_OBJETIVO = 500; // € — fondo fijo de cambio

// ─── DENOMINACIONES ───────────────────────────────────────────────────────────
const BILLETES = [
  { valor: 500, label: '500 €' },
  { valor: 200, label: '200 €' },
  { valor: 100, label: '100 €' },
  { valor: 50, label: '50 €' },
  { valor: 20, label: '20 €' },
  { valor: 10, label: '10 €' },
  { valor: 5, label: '5 €' },
];
const MONEDAS = [
  { valor: 2, label: '2 €' },
  { valor: 1, label: '1 €' },
  { valor: 0.5, label: '0,50 €' },
  { valor: 0.2, label: '0,20 €' },
  { valor: 0.1, label: '0,10 €' },
  { valor: 0.05, label: '0,05 €' },
  { valor: 0.02, label: '0,02 €' },
  { valor: 0.01, label: '0,01 €' },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// All monetary calculations in cents to avoid floating point errors
const toCents = (euros) => Math.round((euros || 0) * 100);
const fromCents = (cents) => cents / 100;

const fmt = (n, d = 2) =>
  typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => (typeof n === 'number' ? `${fmt(n)} €` : '—');
const fmtEurSigned = (n) => {
  if (typeof n !== 'number') return '—';
  const s = fmt(Math.abs(n));
  return n > 0 ? `+${s} €` : n < 0 ? `-${s} €` : `${s} €`;
};

const toMadrid = (ts) => {
  if (!ts) return null;
  try { return new Date(ts)?.toLocaleString('es-ES', { timeZone: 'Europe/Madrid' }); } catch { return ts; }
};
const toMadridDate = (ts) => {
  if (!ts) return null;
  try { return new Date(ts)?.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return ts; }
};
const toMadridTime = (ts) => {
  if (!ts) return null;
  try { return new Date(ts)?.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }); } catch { return ts; }
};
const todayMadrid = () => new Date()?.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

const normalizePago = (metodo_codigo, metodo_nombre) => {
  const code = (metodo_codigo || '')?.toLowerCase();
  const name = (metodo_nombre || '')?.toLowerCase();
  if (code === 't.credito' || code === 'credito' || name?.includes('tarjeta') || name?.includes('credito') || name?.includes('crédito')) return 'Tarjeta';
  if (code === 'efectivo' || name?.includes('efectivo') || name?.includes('cash')) return 'Efectivo';
  if (code === 'bizum' || name?.includes('bizum')) return 'Bizum';
  return metodo_nombre || metodo_codigo || 'Otros';
};

const EstadoBadge = ({ estado }) => {
  const cfg = {
    CUADRADA: { color: ACCENT, label: 'Cuadrada', icon: 'CheckCircle' },
    SOBRANTE: { color: BLUE, label: 'Sobrante', icon: 'TrendingUp' },
    FALTANTE: { color: RED, label: 'Faltante', icon: 'TrendingDown' },
    ABIERTA: { color: ORANGE, label: 'Abierta', icon: 'Clock' },
    CERRADA: { color: '#6B7280', label: 'Cerrada', icon: 'Lock' },
  };
  const c = cfg?.[estado] || cfg?.ABIERTA;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-oranienbaum"
      style={{ background: `${c?.color}15`, border: `1px solid ${c?.color}30`, color: c?.color }}>
      <Icon name={c?.icon} size={10} />{c?.label}
    </span>
  );
};

// ─── PRESET RANGES ────────────────────────────────────────────────────────────
const getPresetRange = (preset) => {
  const now = new Date();
  const tz = 'Europe/Madrid';
  const todayStr = now?.toLocaleDateString('sv-SE', { timeZone: tz });
  const yesterday = new Date(now); yesterday?.setDate(yesterday?.getDate() - 1);
  const yesterdayStr = yesterday?.toLocaleDateString('sv-SE', { timeZone: tz });
  const dayOfWeek = now?.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' });
  const daysMap = { Sun: 6, Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5 };
  const daysFromMonday = daysMap?.[dayOfWeek] ?? 0;
  const weekStart = new Date(now); weekStart?.setDate(weekStart?.getDate() - daysFromMonday);
  const weekStartStr = weekStart?.toLocaleDateString('sv-SE', { timeZone: tz });
  const prevWeekEnd = new Date(weekStart); prevWeekEnd?.setDate(prevWeekEnd?.getDate() - 1);
  const prevWeekStart = new Date(prevWeekEnd); prevWeekStart?.setDate(prevWeekStart?.getDate() - 6);
  const monthStartStr = `${todayStr?.slice(0, 7)}-01`;
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthStartStr = prevMonth?.toLocaleDateString('sv-SE', { timeZone: tz })?.slice(0, 7) + '-01';
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  const prevMonthEndStr = prevMonthEnd?.toLocaleDateString('sv-SE', { timeZone: tz });
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const quarterStartStr = quarterStart?.toLocaleDateString('sv-SE', { timeZone: tz });
  const yearStartStr = `${now?.getFullYear()}-01-01`;

  switch (preset) {
    case 'hoy': return { desde: todayStr, hasta: todayStr };
    case 'ayer': return { desde: yesterdayStr, hasta: yesterdayStr };
    case 'semana': return { desde: weekStartStr, hasta: todayStr };
    case 'semana_anterior': return { desde: prevWeekStart?.toLocaleDateString('sv-SE', { timeZone: tz }), hasta: prevWeekEnd?.toLocaleDateString('sv-SE', { timeZone: tz }) };
    case 'mes': return { desde: monthStartStr, hasta: todayStr };
    case 'mes_anterior': return { desde: prevMonthStartStr, hasta: prevMonthEndStr };
    case 'trimestre': return { desde: quarterStartStr, hasta: todayStr };
    case 'anio': return { desde: yearStartStr, hasta: todayStr };
    case 'mayo_2026': return { desde: '2026-05-01', hasta: '2026-05-31' };
    case 'junio_2026': return { desde: '2026-06-01', hasta: '2026-06-30' };
    case 'julio_2026': return { desde: '2026-07-01', hasta: '2026-07-31' };
    case 'agosto_2026': return { desde: '2026-08-01', hasta: '2026-08-31' };
    case 'historico': return { desde: '2026-05-01', hasta: todayStr };
    default: return { desde: '', hasta: '' };
  }
};

// ─── EPELSA SERVICE (server-side, no Bad Request) ─────────────────────────────
// FIX: KPIs now use server-side RPC functions (SUM/COUNT in PostgreSQL) that
// bypass Supabase's default 1000-row PostgREST limit. With 2163+ tickets the
// old approach only processed ~1000 rows. RPCs aggregate the full dataset.
// Table display remains paginated (no change needed there).
const BATCH_SIZE = 200;

const cajaDiariaService = {
  // ── KPIs via server-side RPC ──────────────────────────────────────────────
  // Uses tienda.get_epelsa_kpis(p_desde, p_hasta) which performs SUM/COUNT
  // over ALL tickets in the range — no row limit applies.
  getKPIs: async (desde, hasta) => {
    if (!desde || !hasta) return { ventas: 0, efectivo: 0, tarjeta: 0, bizum: 0, otros: 0, nTickets: 0, diasConVentas: 0 };
    const { data, error } = await tiendaDb?.rpc('get_epelsa_kpis', {
      p_desde: desde,
      p_hasta: hasta,
    });
    if (error) throw error;
    const row = data?.[0] || {};
    return {
      ventas: parseFloat(row?.total_ventas) || 0,
      nTickets: parseInt(row?.n_tickets) || 0,
      diasConVentas: parseInt(row?.dias_con_ventas) || 0,
      efectivo: parseFloat(row?.efectivo) || 0,
      tarjeta: parseFloat(row?.tarjeta) || 0,
      bizum: parseFloat(row?.bizum) || 0,
      otros: parseFloat(row?.otros) || 0,
    };
  },

  // ── Daily summaries via server-side RPC ───────────────────────────────────
  // Uses tienda.get_epelsa_daily_summaries(p_desde, p_hasta) which groups by
  // Europe/Madrid date and aggregates payments — no row limit applies.
  getDailySummaries: async (desde, hasta) => {
    if (!desde || !hasta) return [];
    const { data, error } = await tiendaDb?.rpc('get_epelsa_daily_summaries', {
      p_desde: desde,
      p_hasta: hasta,
    });
    if (error) throw error;
    return (data || [])?.map((row) => ({
      fecha: row?.fecha,
      nTickets: parseInt(row?.n_tickets) || 0,
      nDevoluciones: parseInt(row?.n_devoluciones) || 0,
      ventasPositivas: parseFloat(row?.ventas_positivas) || 0,
      devoluciones: parseFloat(row?.devoluciones) || 0,
      ventasNetas: parseFloat(row?.ventas_netas) || 0,
      efectivo: parseFloat(row?.efectivo) || 0,
      tarjeta: parseFloat(row?.tarjeta) || 0,
      bizum: parseFloat(row?.bizum) || 0,
      otros: parseFloat(row?.otros) || 0,
    }));
  },

  getArqueos: async (desde, hasta) => {
    const { data, error } = await tiendaDb?.from('caja_diaria')
      ?.select('*')?.gte('fecha', desde)?.lte('fecha', hasta)?.order('fecha', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  upsertArqueo: async (arqueo) => {
    const { id, ...rest } = arqueo;
    const payload = { ...rest, updated_at: new Date()?.toISOString() };
    if (id) {
      const { data, error } = await tiendaDb?.from('caja_diaria')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return data;
    }
    const { data, error } = await tiendaDb?.from('caja_diaria')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },

  getTicketsForDate: async (fecha) => {
    const { data: tickets, error: tErr } = await tiendaDb
      ?.from('epelsa_tickets')
      ?.select('id, numero_ticket, fecha, fecha_operacion, total, vendedor_nombre, vendedor_codigo, lineas_numero, forma_pago')
      ?.eq('fecha_operacion', fecha)
      ?.order('fecha', { ascending: false });
    if (tErr) throw tErr;
    if (!tickets?.length) return [];

    const ticketIds = tickets?.map((t) => t?.id);
    const allPagos = [], allLineas = [];

    for (let i = 0; i < ticketIds?.length; i += BATCH_SIZE) {
      const batch = ticketIds?.slice(i, i + BATCH_SIZE);
      const [{ data: p }, { data: l }] = await Promise.all([
        tiendaDb?.from('epelsa_ticket_pagos')?.select('ticket_id, metodo_codigo, metodo_nombre, importe_entregado')?.in('ticket_id', batch),
        tiendaDb?.from('epelsa_ticket_lineas')?.select('ticket_id, descripcion, cantidad, peso, precio_unitario, total, iva_porcentaje, articulo_codigo, familia_codigo')?.in('ticket_id', batch),
      ]);
      if (p) allPagos?.push(...p);
      if (l) allLineas?.push(...l);
    }

    const pagosByTicket = {}, lineasByTicket = {};
    for (const p of allPagos) { if (!pagosByTicket?.[p?.ticket_id]) pagosByTicket[p.ticket_id] = []; pagosByTicket?.[p?.ticket_id]?.push(p); }
    for (const l of allLineas) { if (!lineasByTicket?.[l?.ticket_id]) lineasByTicket[l.ticket_id] = []; lineasByTicket?.[l?.ticket_id]?.push(l); }

    return tickets?.map((t) => ({ ...t, pagos: pagosByTicket?.[t?.id] || [], lineas: lineasByTicket?.[t?.id] || [] }));
  },
};

// ─── MINI-CALCULADORA DE BILLETES Y MONEDAS ───────────────────────────────────
const MiniCalculadora = ({ onChange, initialValues = {} }) => {
  const [unidades, setUnidades] = useState(() => {
    const init = {};
    [...BILLETES, ...MONEDAS]?.forEach((d) => { init[d.valor] = initialValues?.[d?.valor] ?? 0; });
    return init;
  });
  const inputRefs = useRef({});

  const allDenoms = [...BILLETES, ...MONEDAS];

  const totalBilletesCents = useMemo(() =>
    BILLETES?.reduce((s, d) => s + toCents(d?.valor) * (parseInt(unidades?.[d?.valor]) || 0), 0), [unidades]);
  const totalMonedasCents = useMemo(() =>
    MONEDAS?.reduce((s, d) => s + toCents(d?.valor) * (parseInt(unidades?.[d?.valor]) || 0), 0), [unidades]);
  const totalCents = totalBilletesCents + totalMonedasCents;

  useEffect(() => { onChange?.(fromCents(totalCents), unidades); }, [totalCents]);

  const handleChange = (valor, raw) => {
    const n = Math.max(0, parseInt(raw) || 0);
    setUnidades((prev) => ({ ...prev, [valor]: n }));
  };

  const handleKeyDown = (e, valor) => {
    if (e?.key === 'Enter' || e?.key === 'Tab') {
      e?.preventDefault();
      const idx = allDenoms?.findIndex((d) => d?.valor === valor);
      const next = allDenoms?.[idx + 1];
      if (next) inputRefs?.current?.[next?.valor]?.focus();
    }
  };

  const resetAll = () => {
    const z = {};
    allDenoms?.forEach((d) => { z[d.valor] = 0; });
    setUnidades(z);
  };

  const DenomRow = ({ d }) => {
    const u = parseInt(unidades?.[d?.valor]) || 0;
    const subtotalCents = toCents(d?.valor) * u;
    return (
      <div className="flex items-center gap-2 py-1.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
        <span className="font-oranienbaum text-white/60 text-sm w-16 text-right">{d?.label}</span>
        <span className="font-oranienbaum text-white/30 text-xs">×</span>
        <input
          ref={(el) => { inputRefs.current[d.valor] = el; }}
          type="number" min="0" step="1"
          value={u === 0 ? '' : u}
          placeholder="0"
          onChange={(e) => handleChange(d?.valor, e?.target?.value)}
          onKeyDown={(e) => handleKeyDown(e, d?.valor)}
          onFocus={(e) => e?.target?.select()}
          className="w-16 px-2 py-1 rounded-lg text-center text-sm font-oranienbaum text-white/90 outline-none"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
        />
        <span className="font-oranienbaum text-white/30 text-xs">=</span>
        <span className="flex-1 font-oranienbaum text-sm text-right" style={{ color: subtotalCents > 0 ? GOLD : 'rgba(255,255,255,0.25)' }}>
          {subtotalCents > 0 ? fmtEur(fromCents(subtotalCents)) : '—'}
        </span>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Recuento de Efectivo</p>
        <button onClick={resetAll} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-oranienbaum transition-all"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
          <Icon name="RotateCcw" size={11} />Poner a 0
        </button>
      </div>
      {/* Billetes */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Banknote" size={12} style={{ color: GOLD }} />
          <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">Billetes</span>
        </div>
        {BILLETES?.map((d) => <DenomRow key={d?.valor} d={d} />)}
        <div className="flex justify-between mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${GOLD}20` }}>
          <span className="font-oranienbaum text-white/50 text-xs">Total billetes</span>
          <span className="font-oranienbaum font-bold text-sm" style={{ color: GOLD }}>{fmtEur(fromCents(totalBilletesCents))}</span>
        </div>
      </div>
      {/* Monedas */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Coins" size={12} style={{ color: ACCENT }} />
          <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">Monedas</span>
        </div>
        {MONEDAS?.map((d) => <DenomRow key={d?.valor} d={d} />)}
        <div className="flex justify-between mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${ACCENT}20` }}>
          <span className="font-oranienbaum text-white/50 text-xs">Total monedas</span>
          <span className="font-oranienbaum font-bold text-sm" style={{ color: ACCENT }}>{fmtEur(fromCents(totalMonedasCents))}</span>
        </div>
      </div>
      {/* Total */}
      <div className="p-3 rounded-xl" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}25` }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-oranienbaum text-white/50 text-xs">Total contado</p>
            <p className="font-oranienbaum text-white/30 text-xs">Billetes + Monedas</p>
          </div>
          <span className="font-oranienbaum font-bold text-2xl" style={{ color: GOLD }}>{fmtEur(fromCents(totalCents))}</span>
        </div>
      </div>
    </div>
  );
};

// ─── PROPUESTA DE RETIRADA ─────────────────────────────────────────────────────
const calcularPropuestaRetirada = (retiradaNecesaria, unidades) => {
  if (retiradaNecesaria <= 0) return [];
  let restanteCents = toCents(retiradaNecesaria);
  const propuesta = [];
  // Priorizar retirar denominaciones altas primero
  const ordenRetirada = [...BILLETES, ...MONEDAS]; // ya ordenado de mayor a menor
  for (const d of ordenRetirada) {
    const disponibles = parseInt(unidades?.[d?.valor]) || 0;
    if (disponibles === 0) continue;
    const valorCents = toCents(d?.valor);
    const maxUnidades = Math.floor(restanteCents / valorCents);
    const usar = Math.min(maxUnidades, disponibles);
    if (usar > 0) {
      propuesta?.push({ ...d, usar, subtotal: fromCents(valorCents * usar) });
      restanteCents -= valorCents * usar;
    }
    if (restanteCents <= 0) break;
  }
  return propuesta;
};

// ─── MODAL ARQUEO (APERTURA + CIERRE) ─────────────────────────────────────────
const ArqueoModal = ({ fecha, epelsaDay, existingArqueo, onSave, onClose }) => {
  const [step, setStep] = useState(1); // 1-10 guided flow
  const [apertura, setApertura] = useState(existingArqueo?.apertura ?? FONDO_OBJETIVO);
  const [fondoContadoCents, setFondoContadoCents] = useState(0);
  const [fondoUnidades, setFondoUnidades] = useState({});
  const [efectivoContadoCents, setEfectivoContadoCents] = useState(0);
  const [efectivoUnidades, setEfectivoUnidades] = useState({});
  const [retiradaRealCents, setRetiradaRealCents] = useState(0);
  const [observaciones, setObservaciones] = useState(existingArqueo?.observaciones ?? '');
  const [movimientos, setMovimientos] = useState(existingArqueo?.movimientos ?? []);
  const [newMov, setNewMov] = useState({ concepto: '', importe: '', tipo: 'Gasto' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const efectivoEpelsa = epelsaDay?.efectivo ?? 0;
  const devolucionesEfectivo = epelsaDay?.devoluciones ?? 0;

  const gastosTotal = movimientos?.filter((m) => m?.tipo === 'Gasto')?.reduce((s, m) => s + toCents(parseFloat(m?.importe) || 0), 0);
  const retiradasTotal = movimientos?.filter((m) => m?.tipo === 'Retirada')?.reduce((s, m) => s + toCents(parseFloat(m?.importe) || 0), 0);
  const ingresosExtra = movimientos?.filter((m) => m?.tipo === 'Ingreso extraordinario')?.reduce((s, m) => s + toCents(parseFloat(m?.importe) || 0), 0);

  const aperturaNum = parseFloat(apertura) || 0;
  const efectivoEsperadoCents = toCents(aperturaNum) + toCents(efectivoEpelsa) + ingresosExtra - toCents(devolucionesEfectivo) - gastosTotal - retiradasTotal;
  const efectivoEsperado = fromCents(efectivoEsperadoCents);

  const diferenciaCents = efectivoContadoCents - efectivoEsperadoCents;
  const diferencia = fromCents(diferenciaCents);
  const estadoCierre = diferenciaCents === 0 ? 'CUADRADA' : diferenciaCents > 0 ? 'SOBRANTE' : 'FALTANTE';

  const fondoContado = fromCents(fondoContadoCents);
  const diferenciaApertura = fondoContadoCents - toCents(FONDO_OBJETIVO);

  // Retirada recomendada
  const retiradaRecomendadaCents = Math.max(0, efectivoContadoCents - toCents(FONDO_OBJETIVO));
  const retiradaRecomendada = fromCents(retiradaRecomendadaCents);
  const faltanParaFondo = Math.max(0, toCents(FONDO_OBJETIVO) - efectivoContadoCents);
  const propuestaRetirada = calcularPropuestaRetirada(retiradaRecomendada, efectivoUnidades);

  const addMovimiento = () => {
    if (!newMov?.concepto || !newMov?.importe) return;
    setMovimientos((prev) => [...prev, { ...newMov, importe: parseFloat(newMov?.importe) || 0, hora: new Date()?.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }) }]);
    setNewMov({ concepto: '', importe: '', tipo: 'Gasto' });
  };

  const handleSave = async () => {
    try {
      setSaving(true); setErr(null);
      const gastos = fromCents(gastosTotal + retiradasTotal);
      await onSave({
        id: existingArqueo?.id,
        fecha,
        apertura: aperturaNum,
        efectivo: efectivoEpelsa,
        tarjeta: epelsaDay?.tarjeta ?? 0,
        bizum: epelsaDay?.bizum ?? 0,
        transferencia: epelsaDay?.otros ?? 0,
        gastos,
        cierre: efectivoEsperado,
        efectivo_contado: fromCents(efectivoContadoCents),
        diferencia,
        estado: estadoCierre,
        movimientos,
        observaciones,
      });
      onClose();
    } catch (e) { setErr(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const STEPS = [
    { n: 1, label: 'Ventas Epelsa' },
    { n: 2, label: 'Movimientos' },
    { n: 3, label: 'Ef. Esperado' },
    { n: 4, label: 'Apertura' },
    { n: 5, label: 'Recuento' },
    { n: 6, label: 'Cuadre' },
    { n: 7, label: 'Retirada' },
    { n: 8, label: 'Propuesta' },
    { n: 9, label: 'Confirmar' },
    { n: 10, label: 'Cierre' },
  ];

  const stepColor = (n) => n === step ? GOLD : n < step ? ACCENT : 'rgba(255,255,255,0.20)';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
      <div className="w-full max-w-xl rounded-2xl max-h-[95vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4" style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-lg">Arqueo de Caja</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{fecha} · Fondo objetivo: {fmtEur(FONDO_OBJETIVO)}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
          </div>
          {/* Step indicator */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {STEPS?.map((s, i) => (
              <React.Fragment key={s?.n}>
                <button onClick={() => setStep(s?.n)} className="flex flex-col items-center gap-0.5 flex-shrink-0">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-oranienbaum transition-all"
                    style={{ background: `${stepColor(s?.n)}20`, border: `1px solid ${stepColor(s?.n)}`, color: stepColor(s?.n) }}>
                    {s?.n < step ? '✓' : s?.n}
                  </div>
                  <span className="font-oranienbaum text-white/30 text-[9px] whitespace-nowrap">{s?.label}</span>
                </button>
                {i < STEPS?.length - 1 && <div className="flex-1 h-px min-w-[8px]" style={{ background: s?.n < step ? `${ACCENT}40` : 'rgba(255,255,255,0.08)' }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-4">
          {err && <div className="mb-4 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{err}</div>}

          {/* PASO 1 — Ventas Epelsa */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 1 · Ventas del día (Epelsa)</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Ventas netas', value: fmtEur(epelsaDay?.ventasNetas), color: ACCENT },
                  { label: 'Nº Tickets', value: epelsaDay?.nTickets ?? 0, color: GOLD },
                  { label: 'Efectivo cobrado', value: fmtEur(efectivoEpelsa), color: ACCENT },
                  { label: 'Tarjeta', value: fmtEur(epelsaDay?.tarjeta), color: BLUE },
                  { label: 'Bizum', value: fmtEur(epelsaDay?.bizum), color: PURPLE },
                  { label: 'Otros', value: fmtEur(epelsaDay?.otros), color: '#6B7280' },
                  { label: 'Devoluciones', value: fmtEur(epelsaDay?.devoluciones), color: ORANGE },
                  { label: 'Ventas positivas', value: fmtEur(epelsaDay?.ventasPositivas), color: ACCENT },
                ]?.map((item, i) => (
                  <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="font-oranienbaum text-white/40 text-xs mb-1">{item?.label}</p>
                    <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color }}>{item?.value}</p>
                  </div>
                ))}
              </div>
              <p className="font-oranienbaum text-white/25 text-xs">Datos de solo lectura · Fuente: Epelsa</p>
            </div>
          )}

          {/* PASO 2 — Movimientos de caja */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 2 · Movimientos de caja</p>
              <div className="space-y-2">
                {movimientos?.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <span className="flex-1 font-oranienbaum text-white/70 text-xs">{m?.concepto}</span>
                    <span className="font-oranienbaum text-xs" style={{ color: m?.tipo === 'Ingreso extraordinario' ? ACCENT : RED }}>
                      {m?.tipo === 'Ingreso extraordinario' ? '+' : '-'}{fmtEur(m?.importe)}
                    </span>
                    <span className="font-oranienbaum text-white/30 text-xs">{m?.tipo}</span>
                    <button onClick={() => setMovimientos((p) => p?.filter((_, idx) => idx !== i))} className="text-white/30 hover:text-red-400 transition-colors"><Icon name="X" size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={newMov?.concepto} onChange={(e) => setNewMov((p) => ({ ...p, concepto: e?.target?.value }))}
                  placeholder="Concepto" className="flex-1 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/85 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                <input type="number" step="0.01" value={newMov?.importe} onChange={(e) => setNewMov((p) => ({ ...p, importe: e?.target?.value }))}
                  placeholder="€" className="w-20 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/85 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                <select value={newMov?.tipo} onChange={(e) => setNewMov((p) => ({ ...p, tipo: e?.target?.value }))}
                  className="px-2 py-2 rounded-xl text-xs font-oranienbaum text-white/85 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  <option value="Gasto">Gasto</option>
                  <option value="Retirada">Retirada</option>
                  <option value="Ingreso extraordinario">Ingreso extra</option>
                </select>
                <button onClick={addMovimiento} className="px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                  style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
                  <Icon name="Plus" size={13} />
                </button>
              </div>
              <div className="p-3 rounded-xl text-xs font-oranienbaum space-y-1" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                {gastosTotal > 0 && <div className="flex justify-between text-red-400/70"><span>Gastos</span><span>-{fmtEur(fromCents(gastosTotal))}</span></div>}
                {retiradasTotal > 0 && <div className="flex justify-between text-red-400/70"><span>Retiradas</span><span>-{fmtEur(fromCents(retiradasTotal))}</span></div>}
                {ingresosExtra > 0 && <div className="flex justify-between" style={{ color: ACCENT }}><span>Ingresos extra</span><span>+{fmtEur(fromCents(ingresosExtra))}</span></div>}
              </div>
            </div>
          )}

          {/* PASO 3 — Efectivo esperado */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 3 · Efectivo esperado en caja</p>
              <div className="p-4 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  { label: 'Fondo inicial (apertura)', value: fmtEur(aperturaNum), sign: '' },
                  { label: '+ Efectivo cobrado Epelsa', value: fmtEur(efectivoEpelsa), sign: '+', color: ACCENT },
                  ingresosExtra > 0 && { label: '+ Ingresos extraordinarios', value: fmtEur(fromCents(ingresosExtra)), sign: '+', color: ACCENT },
                  devolucionesEfectivo > 0 && { label: '- Devoluciones efectivo', value: fmtEur(devolucionesEfectivo), sign: '-', color: RED },
                  gastosTotal > 0 && { label: '- Gastos', value: fmtEur(fromCents(gastosTotal)), sign: '-', color: RED },
                  retiradasTotal > 0 && { label: '- Retiradas', value: fmtEur(fromCents(retiradasTotal)), sign: '-', color: RED },
                ]?.filter(Boolean)?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm font-oranienbaum">
                    <span className="text-white/50">{item?.label}</span>
                    <span style={{ color: item?.color || 'rgba(255,255,255,0.70)' }}>{item?.value}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2" style={{ borderTop: `1px solid ${GOLD}30` }}>
                  <span className="font-oranienbaum font-bold text-white">EFECTIVO ESPERADO</span>
                  <span className="font-oranienbaum font-bold text-2xl" style={{ color: GOLD }}>{fmtEur(efectivoEsperado)}</span>
                </div>
              </div>
              <p className="font-oranienbaum text-white/25 text-xs">Este es el dinero que debería haber físicamente en caja.</p>
            </div>
          )}

          {/* PASO 4 — Apertura / Fondo inicial */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 4 · Verificar apertura</p>
              <div className="p-4 rounded-xl" style={{ background: `${BLUE}08`, border: `1px solid ${BLUE}25` }}>
                <p className="font-oranienbaum text-white/50 text-sm mb-1">Fondo objetivo de cambio</p>
                <p className="font-oranienbaum font-bold text-3xl" style={{ color: BLUE }}>{fmtEur(FONDO_OBJETIVO)}</p>
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Fondo inicial real (editable)</label>
                <div className="relative">
                  <input type="number" step="0.01" value={apertura} onChange={(e) => setApertura(e?.target?.value)}
                    className="w-full px-3 py-2 pr-8 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BLUE}40` }} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 font-oranienbaum text-white/30 text-xs">€</span>
                </div>
              </div>
              <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="font-oranienbaum text-white/40 text-xs mb-2">Verificación apertura</p>
                <div className="flex items-center justify-between">
                  <span className="font-oranienbaum text-white/60 text-sm">Fondo esperado</span>
                  <span className="font-oranienbaum text-white/60 text-sm">{fmtEur(FONDO_OBJETIVO)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-oranienbaum text-white/60 text-sm">Fondo real</span>
                  <span className="font-oranienbaum font-bold text-sm" style={{ color: BLUE }}>{fmtEur(aperturaNum)}</span>
                </div>
                {Math.abs(aperturaNum - FONDO_OBJETIVO) < 0.01 ? (
                  <div className="mt-2 flex items-center gap-2" style={{ color: ACCENT }}>
                    <Icon name="CheckCircle" size={14} />
                    <span className="font-oranienbaum text-sm font-bold">APERTURA CORRECTA ✓</span>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2" style={{ color: ORANGE }}>
                    <Icon name="AlertTriangle" size={14} />
                    <span className="font-oranienbaum text-sm">DIFERENCIA APERTURA: {fmtEurSigned(aperturaNum - FONDO_OBJETIVO)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PASO 5 — Recuento físico */}
          {step === 5 && (
            <div>
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-4">Paso 5 · Recuento físico de efectivo</p>
              <MiniCalculadora
                onChange={(total, unids) => { setEfectivoContadoCents(toCents(total)); setEfectivoUnidades(unids); }}
                initialValues={efectivoUnidades}
              />
            </div>
          )}

          {/* PASO 6 — Cuadre */}
          {step === 6 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 6 · Cuadre de caja</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-xl" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}25` }}>
                  <p className="font-oranienbaum text-white/50 text-xs mb-1">Efectivo esperado</p>
                  <p className="font-oranienbaum font-bold text-xl" style={{ color: GOLD }}>{fmtEur(efectivoEsperado)}</p>
                </div>
                <div className="p-4 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}>
                  <p className="font-oranienbaum text-white/50 text-xs mb-1">Efectivo contado</p>
                  <p className="font-oranienbaum font-bold text-xl" style={{ color: ACCENT }}>{fmtEur(fromCents(efectivoContadoCents))}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl text-center" style={{
                background: `${estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED}08`,
                border: `2px solid ${estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED}40`
              }}>
                <p className="font-oranienbaum text-white/50 text-xs mb-1">Diferencia</p>
                <p className="font-oranienbaum font-bold text-3xl mb-2" style={{ color: estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED }}>
                  {fmtEurSigned(diferencia)}
                </p>
                <div className="flex justify-center">
                  {estadoCierre === 'CUADRADA' && <span className="font-oranienbaum font-bold text-lg" style={{ color: ACCENT }}>✓ CAJA CUADRADA</span>}
                  {estadoCierre === 'SOBRANTE' && <span className="font-oranienbaum font-bold text-lg" style={{ color: BLUE }}>SOBRANTE {fmtEurSigned(diferencia)}</span>}
                  {estadoCierre === 'FALTANTE' && <span className="font-oranienbaum font-bold text-lg" style={{ color: RED }}>FALTANTE {fmtEurSigned(diferencia)}</span>}
                </div>
              </div>
            </div>
          )}

          {/* PASO 7 — Retirada */}
          {step === 7 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 7 · Retirada para dejar {fmtEur(FONDO_OBJETIVO)}</p>
              {retiradaRecomendadaCents > 0 ? (
                <>
                  <div className="p-4 rounded-xl text-center" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}30` }}>
                    <p className="font-oranienbaum text-white/50 text-xs mb-1">RETIRAR</p>
                    <p className="font-oranienbaum font-bold text-3xl" style={{ color: GOLD }}>{fmtEur(retiradaRecomendada)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <p className="font-oranienbaum text-white/40 text-xs mb-1">Efectivo contado</p>
                      <p className="font-oranienbaum font-bold text-lg text-white">{fmtEur(fromCents(efectivoContadoCents))}</p>
                    </div>
                    <div className="p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}>
                      <p className="font-oranienbaum text-white/40 text-xs mb-1">Dejar en caja</p>
                      <p className="font-oranienbaum font-bold text-lg" style={{ color: ACCENT }}>{fmtEur(FONDO_OBJETIVO)}</p>
                    </div>
                  </div>
                </>
              ) : faltanParaFondo > 0 ? (
                <div className="p-4 rounded-xl" style={{ background: `${ORANGE}08`, border: `1px solid ${ORANGE}30` }}>
                  <Icon name="AlertTriangle" size={20} style={{ color: ORANGE }} className="mb-2" />
                  <p className="font-oranienbaum font-bold text-lg" style={{ color: ORANGE }}>
                    FALTAN {fmtEur(fromCents(faltanParaFondo))} PARA COMPLETAR EL FONDO DE CAJA
                  </p>
                  <p className="font-oranienbaum text-white/40 text-xs mt-1">Fondo objetivo: {fmtEur(FONDO_OBJETIVO)} · Contado: {fmtEur(fromCents(efectivoContadoCents))}</p>
                </div>
              ) : (
                <div className="p-4 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}>
                  <p className="font-oranienbaum font-bold text-lg" style={{ color: ACCENT }}>✓ No es necesario retirar efectivo</p>
                  <p className="font-oranienbaum text-white/40 text-xs mt-1">El efectivo contado es exactamente el fondo objetivo.</p>
                </div>
              )}
            </div>
          )}

          {/* PASO 8 — Propuesta de retirada */}
          {step === 8 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 8 · Propuesta de retirada (orientativa)</p>
              {propuestaRetirada?.length > 0 ? (
                <>
                  <p className="font-oranienbaum text-white/40 text-xs">Retirada necesaria: <span style={{ color: GOLD }}>{fmtEur(retiradaRecomendada)}</span></p>
                  <div className="space-y-1">
                    {propuestaRetirada?.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <span className="font-oranienbaum text-white/60 text-sm w-16 text-right">{item?.usar} × {item?.label}</span>
                        <span className="font-oranienbaum text-white/30 text-xs">=</span>
                        <span className="font-oranienbaum font-bold text-sm" style={{ color: GOLD }}>{fmtEur(item?.subtotal)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between px-3 py-2 rounded-xl mt-1" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}25` }}>
                      <span className="font-oranienbaum font-bold text-white text-sm">TOTAL RETIRADA</span>
                      <span className="font-oranienbaum font-bold text-sm" style={{ color: GOLD }}>{fmtEur(propuestaRetirada?.reduce((s, i) => s + i?.subtotal, 0))}</span>
                    </div>
                  </div>
                  <p className="font-oranienbaum text-white/25 text-xs">Esta propuesta es orientativa. Solo usa denominaciones que has contado.</p>
                </>
              ) : (
                <p className="font-oranienbaum text-white/40 text-sm">No hay retirada necesaria o no hay denominaciones suficientes para la propuesta.</p>
              )}
            </div>
          )}

          {/* PASO 9 — Confirmar retirada real */}
          {step === 9 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 9 · Confirmar retirada realizada</p>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Importe retirado realmente</label>
                <div className="relative">
                  <input type="number" step="0.01" value={retiradaRealCents / 100 || ''} placeholder={retiradaRecomendada}
                    onChange={(e) => setRetiradaRealCents(toCents(parseFloat(e?.target?.value) || 0))}
                    className="w-full px-3 py-2 pr-8 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${GOLD}40` }} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 font-oranienbaum text-white/30 text-xs">€</span>
                </div>
                <p className="font-oranienbaum text-white/30 text-xs mt-1">Retirada recomendada: {fmtEur(retiradaRecomendada)}</p>
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Observaciones</label>
                <textarea value={observaciones} onChange={(e) => setObservaciones(e?.target?.value)} rows={2}
                  className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
              </div>
            </div>
          )}

          {/* PASO 10 — Resumen final */}
          {step === 10 && (
            <div className="space-y-4">
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest">Paso 10 · Resumen de cierre</p>
              <div className="p-4 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  { label: 'FONDO INICIAL', value: fmtEur(aperturaNum), color: BLUE },
                  { label: 'EFECTIVO COBRADO EPELSA', value: fmtEur(efectivoEpelsa), color: ACCENT },
                  { label: 'EFECTIVO ESPERADO', value: fmtEur(efectivoEsperado), color: GOLD },
                  { label: 'EFECTIVO CONTADO', value: fmtEur(fromCents(efectivoContadoCents)), color: ACCENT },
                  { label: 'DIFERENCIA', value: fmtEurSigned(diferencia), color: estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED },
                  { label: 'RETIRAR', value: fmtEur(retiradaRecomendada), color: GOLD },
                  { label: 'DEJAR EN CAJA', value: fmtEur(FONDO_OBJETIVO), color: ACCENT },
                ]?.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <span className="font-oranienbaum text-white/50 text-xs">{item?.label}</span>
                    <span className="font-oranienbaum font-bold text-sm" style={{ color: item?.color }}>{item?.value}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-xl text-center" style={{
                background: `${estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED}08`,
                border: `2px solid ${estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED}40`
              }}>
                <EstadoBadge estado={estadoCierre} />
                <p className="font-oranienbaum font-bold text-xl mt-2" style={{ color: estadoCierre === 'CUADRADA' ? ACCENT : estadoCierre === 'SOBRANTE' ? BLUE : RED }}>
                  {estadoCierre === 'CUADRADA' ? '✓ CAJA CUADRADA' : estadoCierre === 'SOBRANTE' ? `SOBRANTE ${fmtEurSigned(diferencia)}` : `FALTANTE ${fmtEurSigned(diferencia)}`}
                </p>
              </div>
              <div className="p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}>
                <p className="font-oranienbaum text-white/50 text-xs mb-1">FONDO FINAL DE CAJA</p>
                <p className="font-oranienbaum font-bold text-2xl" style={{ color: ACCENT }}>{fmtEur(FONDO_OBJETIVO)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="sticky bottom-0 flex gap-3 px-6 py-4" style={{ background: '#141414', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>Cancelar</button>
          {step > 1 && (
            <button onClick={() => setStep((s) => s - 1)} className="px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
              <Icon name="ChevronLeft" size={14} className="inline mr-1" />Anterior
            </button>
          )}
          <div className="flex-1" />
          {step < 10 ? (
            <button onClick={() => setStep((s) => s + 1)} className="px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
              style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
              Siguiente<Icon name="ChevronRight" size={14} className="inline ml-1" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-oranienbaum transition-all disabled:opacity-50"
              style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
              {saving ? 'Guardando...' : '✓ Registrar Cierre'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DETAIL MODAL ─────────────────────────────────────────────────────────────
const DetailModal = ({ fecha, epelsaDay, arqueo, onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    cajaDiariaService?.getTicketsForDate(fecha)?.then(setTickets)?.catch(() => setTickets([]))?.finally(() => setLoadingTickets(false));
  }, [fecha]);

  const movimientos = arqueo?.movimientos || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4" style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">Detalle del día</h3>
            <p className="font-oranienbaum text-white/40 text-xs">{toMadridDate(fecha + 'T12:00:00')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-5">
          {/* Resumen */}
          <div>
            <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-3">Resumen</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { label: 'Ventas netas', value: fmtEur(epelsaDay?.ventasNetas), color: ACCENT },
                { label: 'Nº tickets', value: epelsaDay?.nTickets ?? 0, color: GOLD },
                { label: 'Efectivo Epelsa', value: fmtEur(epelsaDay?.efectivo), color: ACCENT },
                { label: 'Tarjeta', value: fmtEur(epelsaDay?.tarjeta), color: BLUE },
                { label: 'Bizum', value: fmtEur(epelsaDay?.bizum), color: PURPLE },
                { label: 'Otros', value: fmtEur(epelsaDay?.otros), color: '#6B7280' },
                { label: 'Devoluciones', value: fmtEur(epelsaDay?.devoluciones), color: RED },
                { label: 'Apertura', value: fmtEur(arqueo?.apertura), color: BLUE },
                { label: 'Ef. esperado', value: fmtEur(arqueo?.cierre), color: GOLD },
                { label: 'Ef. contado', value: fmtEur(arqueo?.efectivo_contado), color: ACCENT },
                { label: 'Diferencia', value: arqueo?.diferencia != null ? fmtEurSigned(arqueo?.diferencia) : '—', color: arqueo?.diferencia === 0 ? ACCENT : arqueo?.diferencia > 0 ? BLUE : RED },
                { label: 'Estado', value: arqueo ? <EstadoBadge estado={arqueo?.estado} /> : '—', color: null },
              ]?.map((item, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-oranienbaum text-white/40 text-xs mb-1">{item?.label}</p>
                  <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color || 'white' }}>{item?.value}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Movimientos */}
          {movimientos?.length > 0 && (
            <div>
              <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-3">Movimientos de caja</p>
              <div className="space-y-1">
                {movimientos?.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="font-oranienbaum text-white/30 text-xs w-12">{m?.hora || '—'}</span>
                    <span className="flex-1 font-oranienbaum text-white/70 text-xs">{m?.concepto}</span>
                    <span className="font-oranienbaum text-white/40 text-xs">{m?.tipo}</span>
                    <span className="font-oranienbaum text-xs font-bold" style={{ color: m?.tipo === 'Ingreso extraordinario' ? ACCENT : RED }}>
                      {m?.tipo === 'Ingreso extraordinario' ? '+' : '-'}{fmtEur(m?.importe)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Tickets */}
          <div>
            <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-3">Tickets Epelsa ({tickets?.length})</p>
            {loadingTickets ? (
              <div className="flex justify-center py-6"><div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" /></div>
            ) : tickets?.length === 0 ? (
              <p className="font-oranienbaum text-white/30 text-sm text-center py-4">Sin tickets</p>
            ) : (
              <div className="space-y-1">
                {tickets?.map((t) => {
                  const isDevolucion = parseFloat(t?.total) < 0;
                  const expanded = expandedTicket === t?.id;
                  return (
                    <div key={t?.id}>
                      <button onClick={() => setExpandedTicket(expanded ? null : t?.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors hover:bg-white/[0.03]"
                        style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${isDevolucion ? `${ORANGE}20` : 'rgba(255,255,255,0.05)'}` }}>
                        <span className="font-oranienbaum text-white/50 text-xs w-16">#{t?.numero_ticket}</span>
                        <span className="font-oranienbaum text-white/50 text-xs w-14">{toMadridTime(t?.fecha)}</span>
                        <span className="flex-1 font-oranienbaum text-white/60 text-xs">{t?.vendedor_nombre || t?.vendedor_codigo || '—'}</span>
                        <span className="font-oranienbaum text-white/40 text-xs">{t?.lineas_numero ?? t?.lineas?.length ?? 0} líneas</span>
                        {isDevolucion && <span className="font-oranienbaum text-xs px-1.5 py-0.5 rounded" style={{ background: `${ORANGE}15`, color: ORANGE }}>Dev.</span>}
                        <span className="font-oranienbaum font-bold text-sm" style={{ color: isDevolucion ? ORANGE : 'white' }}>{fmtEur(parseFloat(t?.total))}</span>
                        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={12} className="text-white/30" />
                      </button>
                      {expanded && (
                        <div className="mx-2 mb-1 p-3 rounded-b-xl" style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', borderTop: 'none' }}>
                          {t?.pagos?.length > 0 && (
                            <div className="mb-2">
                              <p className="font-oranienbaum text-white/30 text-xs mb-1">Pagos</p>
                              {t?.pagos?.map((p, pi) => (
                                <div key={pi} className="flex justify-between text-xs font-oranienbaum text-white/50">
                                  <span>{normalizePago(p?.metodo_codigo, p?.metodo_nombre)}</span>
                                  <span>{fmtEur(parseFloat(p?.importe_entregado))}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {t?.lineas?.length > 0 && (
                            <div>
                              <p className="font-oranienbaum text-white/30 text-xs mb-1">Líneas</p>
                              {t?.lineas?.map((l, li) => (
                                <div key={li} className="flex items-center gap-2 text-xs font-oranienbaum text-white/50 py-0.5">
                                  <span className="flex-1 truncate">{l?.descripcion || l?.articulo_codigo || '—'}</span>
                                  {l?.peso > 0 && <span>{fmt(l?.peso, 3)} kg</span>}
                                  {l?.cantidad > 0 && <span>×{fmt(l?.cantidad, 0)}</span>}
                                  <span>{fmtEur(parseFloat(l?.total))}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TiendaCajaDiaria = () => {
  const today = todayMadrid();
  const [preset, setPreset] = useState('mes');
  const [desde, setDesde] = useState(() => getPresetRange('mes')?.desde);
  const [hasta, setHasta] = useState(() => getPresetRange('mes')?.hasta);
  const [filtroEstado, setFiltroEstado] = useState('Todas');
  const [filtroPago, setFiltroPago] = useState('Todos');
  const [epelsaSummaries, setEpelsaSummaries] = useState([]);
  const [arqueos, setArqueos] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [arqueoModal, setArqueoModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const loadData = useCallback(async () => {
    if (!desde || !hasta) return;
    try {
      setLoading(true); setError(null);
      const [summaries, arqueoData, kpiData] = await Promise.all([
        cajaDiariaService?.getDailySummaries(desde, hasta),
        cajaDiariaService?.getArqueos(desde, hasta),
        cajaDiariaService?.getKPIs(desde, hasta),
      ]);
      setEpelsaSummaries(summaries);
      setArqueos(arqueoData);
      setKpis(kpiData);
    } catch (e) { setError(e?.message || 'Error al cargar datos'); }
    finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { loadData(); }, [loadData]);

  const applyPreset = (p) => {
    setPreset(p);
    const r = getPresetRange(p);
    setDesde(r?.desde); setHasta(r?.hasta);
  };

  const mergedRows = useMemo(() => {
    const arqueoByFecha = {};
    for (const a of arqueos) arqueoByFecha[a.fecha] = a;
    const allDates = new Set([...epelsaSummaries.map((s) => s.fecha), ...arqueos.map((a) => a.fecha)]);
    return Array.from(allDates)?.sort((a, b) => b?.localeCompare(a))?.map((fecha) => ({
      fecha,
      epelsa: epelsaSummaries?.find((s) => s?.fecha === fecha) || null,
      arqueo: arqueoByFecha?.[fecha] || null,
    }));
  }, [epelsaSummaries, arqueos]);

  const filteredRows = useMemo(() => mergedRows?.filter((row) => {
    if (filtroEstado !== 'Todas') {
      const estado = row?.arqueo?.estado || 'ABIERTA';
      if (filtroEstado === 'Cuadradas' && estado !== 'CUADRADA') return false;
      if (filtroEstado === 'Con sobrante' && estado !== 'SOBRANTE') return false;
      if (filtroEstado === 'Con faltante' && estado !== 'FALTANTE') return false;
      if (filtroEstado === 'Sin cerrar' && ['CUADRADA', 'SOBRANTE', 'FALTANTE']?.includes(estado)) return false;
      if (filtroEstado === 'Sin arqueo' && row?.arqueo) return false;
    }
    if (filtroPago !== 'Todos' && row?.epelsa) {
      if (filtroPago === 'Efectivo' && (row?.epelsa?.efectivo || 0) === 0) return false;
      if (filtroPago === 'Tarjeta' && (row?.epelsa?.tarjeta || 0) === 0) return false;
      if (filtroPago === 'Bizum' && (row?.epelsa?.bizum || 0) === 0) return false;
      if (filtroPago === 'Otros' && (row?.epelsa?.otros || 0) === 0) return false;
    }
    return true;
  }), [mergedRows, filtroEstado, filtroPago]);

  const gastosTotales = useMemo(() => arqueos?.reduce((s, a) => s + (parseFloat(a?.gastos) || 0), 0), [arqueos]);
  const diferenciaCaja = useMemo(() => {
    const withArqueo = arqueos?.filter((a) => a?.diferencia != null);
    if (!withArqueo?.length) return null;
    return withArqueo?.reduce((s, a) => s + (parseFloat(a?.diferencia) || 0), 0);
  }, [arqueos]);

  const handleSaveArqueo = async (data) => { await cajaDiariaService?.upsertArqueo(data); await loadData(); };

  // Reconciliation alert: payments sum vs net sales
  const pagosTotales = kpis ? (kpis?.efectivo + kpis?.tarjeta + kpis?.bizum + kpis?.otros) : null;
  const diferenciaConciliacion = pagosTotales != null && kpis ? pagosTotales - kpis?.ventas : null;
  const showConciliacionAlert = diferenciaConciliacion != null && Math.abs(diferenciaConciliacion) > 0.05;

  const kpiCards = [
    { label: 'Ventas / Ingresos', value: kpis ? fmtEur(kpis?.ventas) : '—', icon: 'TrendingUp', color: ACCENT, sub: 'Facturación neta Epelsa' },
    { label: 'Tickets', value: kpis ? (kpis?.nTickets?.toLocaleString('es-ES')) : '—', icon: 'Receipt', color: GOLD, sub: 'Total del periodo' },
    { label: 'Días con ventas', value: kpis ? (kpis?.diasConVentas?.toLocaleString('es-ES')) : '—', icon: 'CalendarDays', color: BLUE, sub: 'Días únicos (Madrid)' },
    { label: 'Efectivo', value: kpis ? fmtEur(kpis?.efectivo) : '—', icon: 'Banknote', color: GOLD, sub: 'Cobrado en efectivo' },
    { label: 'Tarjeta', value: kpis ? fmtEur(kpis?.tarjeta) : '—', icon: 'CreditCard', color: BLUE, sub: 'Cobrado con tarjeta' },
    { label: 'Bizum', value: kpis ? fmtEur(kpis?.bizum) : '—', icon: 'Smartphone', color: PURPLE, sub: 'Cobrado por Bizum' },
    { label: 'Otros medios', value: kpis ? fmtEur(kpis?.otros) : '—', icon: 'Wallet', color: ORANGE, sub: 'Otros métodos de pago' },
    { label: 'Gastos / Retiradas', value: fmtEur(gastosTotales), icon: 'ArrowDownCircle', color: RED, sub: 'Movimientos manuales' },
  ];

  const PRESETS = [
    { key: 'hoy', label: 'Hoy' }, { key: 'ayer', label: 'Ayer' },
    { key: 'semana', label: 'Esta semana' }, { key: 'semana_anterior', label: 'Semana anterior' },
    { key: 'mes', label: 'Este mes' }, { key: 'mes_anterior', label: 'Mes anterior' },
    { key: 'trimestre', label: 'Trimestre' }, { key: 'anio', label: 'Año' },
    { key: 'mayo_2026', label: 'Mayo 2026' }, { key: 'junio_2026', label: 'Junio 2026' },
    { key: 'julio_2026', label: 'Julio 2026' }, { key: 'agosto_2026', label: 'Agosto 2026' },
    { key: 'historico', label: 'Histórico completo' },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {arqueoModal && (
        <ArqueoModal fecha={arqueoModal?.fecha} epelsaDay={arqueoModal?.epelsaDay} existingArqueo={arqueoModal?.arqueo}
          onSave={handleSaveArqueo} onClose={() => setArqueoModal(null)} />
      )}
      {detailModal && (
        <DetailModal fecha={detailModal?.fecha} epelsaDay={detailModal?.epelsaDay} arqueo={detailModal?.arqueo}
          onClose={() => setDetailModal(null)} />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="Wallet" size={18} style={{ color: GOLD }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Caja Diaria</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Control de caja · Fondo objetivo: {fmtEur(FONDO_OBJETIVO)} · Datos reales Epelsa</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setArqueoModal({ fecha: today, epelsaDay: epelsaSummaries?.find((s) => s?.fecha === today) || null, arqueo: arqueos?.find((a) => a?.fecha === today) || null })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
            <Icon name="Plus" size={15} />Registrar Caja
          </button>
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={13} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      {showConciliacionAlert && (
        <div className="mb-4 p-3 rounded-xl flex items-start gap-3" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)' }}>
          <Icon name="AlertTriangle" size={15} style={{ color: ORANGE, flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="font-oranienbaum text-sm" style={{ color: ORANGE }}>Alerta de conciliación</p>
            <p className="font-oranienbaum text-white/50 text-xs mt-0.5">
              Suma de medios de pago ({fmtEur(pagosTotales)}) difiere de ventas netas ({fmtEur(kpis?.ventas)}) en {fmtEurSigned(diferenciaConciliacion)}.
              Puede deberse a tickets sin pago registrado o pagos parciales.
            </p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : kpi?.value}</p>
            <div>
              <p className="font-oranienbaum text-white/60 text-xs leading-tight">{kpi?.label}</p>
              <p className="font-oranienbaum text-white/30 text-xs leading-tight">{kpi?.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex flex-wrap gap-2 mb-3">
          {PRESETS?.map((p) => (
            <button key={p?.key} onClick={() => applyPreset(p?.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={{ background: preset === p?.key ? `${GOLD}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${preset === p?.key ? `${GOLD}40` : 'rgba(255,255,255,0.08)'}`, color: preset === p?.key ? GOLD : 'rgba(255,255,255,0.50)' }}>
              {p?.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <label className="font-oranienbaum text-white/40 text-xs">Desde</label>
            <input type="date" value={desde} onChange={(e) => { setPreset(''); setDesde(e?.target?.value); }}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-oranienbaum text-white/40 text-xs">Hasta</label>
            <input type="date" value={hasta} onChange={(e) => { setPreset(''); setHasta(e?.target?.value); }}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
          </div>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e?.target?.value)}
            className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
            {['Todas', 'Cuadradas', 'Con sobrante', 'Con faltante', 'Sin cerrar', 'Sin arqueo']?.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={filtroPago} onChange={(e) => setFiltroPago(e?.target?.value)}
            className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
            {['Todos', 'Efectivo', 'Tarjeta', 'Bizum', 'Otros']?.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/50 text-xs">{filteredRows?.length} días · Fondo objetivo: {fmtEur(FONDO_OBJETIVO)}</span>
          <span className="font-oranienbaum text-white/30 text-xs">Datos reales Epelsa · Solo lectura</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Fecha', 'Nº Tickets', 'Ventas netas', 'Efectivo', 'Tarjeta', 'Bizum', 'Otros', 'Devoluciones', 'Gastos/Ret.', 'Ef. real', 'Diferencia', 'Estado', 'Acciones']?.map((col) => (
                  <th key={col} className="px-3 py-3 text-left whitespace-nowrap">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
                    <p className="font-oranienbaum text-white/40 text-sm">Cargando datos Epelsa...</p>
                  </div>
                </td></tr>
              ) : filteredRows?.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
                      <Icon name="Wallet" size={24} style={{ color: `${GOLD}40` }} />
                    </div>
                    <p className="font-oranienbaum text-white/40 text-sm">Sin datos para el periodo seleccionado</p>
                  </div>
                </td></tr>
              ) : filteredRows?.map((row, i) => {
                const { fecha, epelsa, arqueo } = row;
                const estado = arqueo?.estado || (epelsa ? 'ABIERTA' : null);
                const diferencia = arqueo?.diferencia;
                const isToday = fecha === today;
                return (
                  <tr key={fecha} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: isToday ? `${GOLD}04` : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                    className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-oranienbaum text-white/85 text-sm">{toMadridDate(fecha + 'T12:00:00')}</span>
                        {isToday && <span className="font-oranienbaum text-xs px-1.5 py-0.5 rounded" style={{ background: `${GOLD}15`, color: GOLD }}>Hoy</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{epelsa?.nTickets ?? '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum font-bold text-white text-sm">{epelsa ? fmtEur(epelsa?.ventasNetas) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{epelsa ? fmtEur(epelsa?.efectivo) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{epelsa ? fmtEur(epelsa?.tarjeta) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{epelsa ? fmtEur(epelsa?.bizum) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-sm">{epelsa ? fmtEur(epelsa?.otros) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-sm" style={{ color: (epelsa?.devoluciones || 0) > 0 ? ORANGE : 'rgba(255,255,255,0.40)' }}>{epelsa ? fmtEur(epelsa?.devoluciones) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-sm" style={{ color: (arqueo?.gastos || 0) > 0 ? RED : 'rgba(255,255,255,0.40)' }}>{arqueo ? fmtEur(arqueo?.gastos) : '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-sm" style={{ color: arqueo?.efectivo_contado != null ? ACCENT : 'rgba(255,255,255,0.30)' }}>{arqueo?.efectivo_contado != null ? fmtEur(arqueo?.efectivo_contado) : '—'}</span></td>
                    <td className="px-3 py-3">
                      {diferencia != null ? (
                        <span className="font-oranienbaum font-bold text-sm" style={{ color: diferencia === 0 ? ACCENT : diferencia > 0 ? BLUE : RED }}>{fmtEurSigned(diferencia)}</span>
                      ) : <span className="font-oranienbaum text-white/30 text-sm">—</span>}
                    </td>
                    <td className="px-3 py-3">{estado ? <EstadoBadge estado={estado} /> : <span className="font-oranienbaum text-white/20 text-xs">—</span>}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setArqueoModal({ fecha, epelsaDay: epelsa, arqueo })}
                          className="p-1.5 rounded-lg text-white/30 hover:text-white/70 transition-colors"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }} title={arqueo ? 'Editar arqueo' : 'Registrar arqueo'}>
                          <Icon name={arqueo ? 'Pencil' : 'Plus'} size={12} />
                        </button>
                        <button onClick={() => setDetailModal({ fecha, epelsaDay: epelsa, arqueo })}
                          className="p-1.5 rounded-lg text-white/30 hover:text-white/70 transition-colors"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }} title="Ver detalle">
                          <Icon name="Eye" size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TiendaCajaDiaria;
